import { LicenseInfo } from "@mui/x-license-pro";
import "@webscopeio/react-textarea-autocomplete/style.css";
import "emoji-mart/css/emoji-mart.css";
import { ReclaimAppProps } from "next/app";
import Head from "next/head";
import { App } from "../components/App";
import ClientOnly from "../components/ClientOnly";
import { AnalyticsContextProvider } from "../context/AnalyticsContext";
import { AppContextProvider } from "../context/AppContext";
import { DomainContext } from "../context/DomainContext";
import { UserContextProvider } from "../context/UserContext";
import { LocalStorageSharedContextProvider } from "../hooks/useLocalStorage";
import "../index.css";
import "../lib/wdyr";
import { reclaim } from "../reclaim-api/index";
import { usePageSupportsIntercom } from "@hooks/useIntercom";
// eslint-disable-next-line no-restricted-imports
import { IntercomProvider } from "react-use-intercom";
import { useCallbackSafeRef } from "src/hooks/useCallbackSafeRef";

LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_VERCEL_MATERIAL_KEY || "");

const HOST = process.env.NEXT_PUBLIC_VERCEL_URL || "app.reclaim.ai";
const INTERCOM_APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID;

function MyApp({ Component, pageProps, err }: ReclaimAppProps) {
  const ogTitle: string = pageProps.ogTitle ? pageProps.ogTitle : "Reclaim | A smart friend for your calendar";
  const ogDescription: string = pageProps.ogDescription
    ? pageProps.ogDescription
    : "Reclaim automatically blocks flexible time on your calendar to keep you focused on the things that matter most.";
  const ogImage: string = pageProps.ogImage ? pageProps.ogImage : `https://${HOST}/img/opengraph/general.png`;
  const twitterTitle: string = pageProps.twitterTitle
    ? pageProps.twitterTitle
    : "Reclaim | A smart friend for your calendar";
  const twitterDescription: string = pageProps.twitterDescription
    ? pageProps.twitterDescription
    : "Reclaim automatically blocks flexible time on your calendar to keep you focused on the things that matter most.";
  const twitterImage: string = pageProps.twitterImage
    ? pageProps.twitterImage
    : `https://${HOST}/img/opengraph/general.png`;

  const reloadIfMobile = useCallbackSafeRef(() => {
    // Known issue with Intercom where closing the messenger on mobile breaks styles
    // Fix for now by forcing reload on close of mobile messenger
    // https://github.com/devrnt/react-use-intercom/issues/514
    // - CK 8/23
    if (typeof window !== "undefined") {
      // this class name appears if Intercom is mobile mode
      if (!!document.querySelector(".intercom-w2ttxp")) {
        void window.location.reload();
      }
    }
  });

  const pageSupportsIntercom = usePageSupportsIntercom();

  return (
    <>
      <Head>
        <title>Reclaim – A smart friend for your calendar</title>
        <meta name="viewport" content="minimum-scale=1, initial-scale=1.0, width=device-width" />

        {/* <!-- Primary Meta Tags --> */}
        <meta name="title" content="Reclaim | A smart friend for your calendar" key="title" />
        <meta
          name="description"
          content="Reclaim automatically blocks flexible time on your calendar to keep you focused on the things that matter most."
          key="description"
        />

        {/**
         * Social Sharing Metadata... this is in _app.tsx rather than _document.tsx because here it can be overridden
         * as discussed here: https://github.com/vercel/next.js/issues/9794
         *
         * If you wish to override any of these meta tags, the following keys need to be used:
         *
         *  - ogType
         *  - ogUrl
         *  - ogTitle
         *  - ogDescription
         *  - ogImage
         *  - twitterCard
         *  - twitterUrl
         *  - twitterTitle
         *  - twitterDescription
         *  - twitterImage
         *
         *  To test these out, the following URLs are handy:
         *
         *  https://ogp.me/
         *  https://cards-dev.twitter.com/validator
         *  https://www.linkedin.com/post-inspector/inspect
         */}

        {/*
         * The above note seems incorrect
         * To override og meta tags we must use getServerSideProps
         * As discussed here: https://github.com/vercel/next.js/issues/35172
         * C.K. 11/2022
         */}

        {/* OpenGraph / Slack / etc */}
        <meta name="og:type" content="website" key="ogType" />
        <meta name="og:url" content={`https://${HOST}/`} key="ogUrl" />

        <meta name="og:title" property="og:title" key="ogTitle" content={ogTitle} />
        <meta name="og:description" property="og:description" key="ogDescription" content={ogDescription} />
        <meta name="og:image" property="og:image" key="ogImage" content={ogImage} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" key="twitterCard" />
        <meta name="twitter:url" content={`https://${HOST}/`} key="twitterUrl" />

        <meta name="twitter:title" key="twitterTitle" content={twitterTitle} />
        <meta name="twitter:description" key="twitterDescription" content={twitterDescription} />
        <meta name="twitter:image" key="twitterImage" content={twitterImage} />
        {/***** END SOCIAL SHARING METADATA *****/}
      </Head>

      <DomainContext.Provider value={reclaim}>
        <AnalyticsContextProvider>
          <UserContextProvider>
            <AppContextProvider>
              <LocalStorageSharedContextProvider>
                <IntercomProvider appId={`${INTERCOM_APP_ID}`} autoBoot={pageSupportsIntercom} onHide={reloadIfMobile}>
                  <App>
                    <ClientOnly>
                      <Component {...pageProps} err={err} />
                    </ClientOnly>
                  </App>
                </IntercomProvider>
              </LocalStorageSharedContextProvider>
            </AppContextProvider>
          </UserContextProvider>
        </AnalyticsContextProvider>
      </DomainContext.Provider>
    </>
  );
}

export default MyApp;
